// Atlas manifest resolution (11 §4.1: CELL is read from the atlas manifest). Prefers the Oryx
// atlases in public/assets/oryx (`picks.json` maps game concepts to hand-picked frame ids;
// `manifest.json` carries CELL); falls back to the placeholder atlas from
// scripts/make-dev-atlas.mjs. Texture keys are configurable through the manifest so switching
// art is a data change, and the scene only ever asks for roles (config.ts ROLES).
import { kitKeyOf } from './board/heroTextures';
import { DEFAULT_CELL, DEV_MANIFEST_URL, ORYX_ATLAS_KEYS, ORYX_MANIFEST_URL, ORYX_PICKS_URL, ROLES, type AtlasManifest, type Role, type RoleFrame } from '../config';
import { hugeArt } from './hugeArt';
import { creatureArt } from './creatureArt';

/** Frame-name prefix → atlas key (tools/oryx-extract.py naming). */
export function atlasForFrame(frame: string): string {
  if (frame.startsWith('creature_')) return 'creatures';
  if (frame.startsWith('world_')) return 'world';
  if (frame.startsWith('tile_')) return 'tiles';
  if (frame.startsWith('item_')) return 'items';
  if (frame.startsWith('fx32_')) return 'fx32';
  if (frame.startsWith('fx24_')) return 'fx24';
  if (frame.startsWith('class_')) return 'classes';
  return 'world';
}

/** Which picks.json concepts fill each role, in order of preference. */
const ROLE_PICKS: Record<Role, string[]> = {
  floor: ['floor_stone', 'floor_stone_olive', 'floor_dirt'],
  floor_alt: ['floor_stone_dark', 'floor_mud', 'floor_stone'],
  walkway: ['wood_walkway_ew', 'bridge_plank_ew', 'floor_wood'],
  wall: ['wall_stone_face', 'wall_stone_set'],
  water: ['water', 'water_pool_1x1'],
  door: ['door_closed', 'door_iron_closed', 'gate_closed'],
  exit: ['stairs_down', 'stairs_up', 'floor_stone'],
  hero_fighter: ['hero_fighter'], hero_wizard: ['hero_wizard'], hero_cleric: ['hero_cleric'], hero_ranger: ['hero_ranger'],
  goblin: ['goblin_screener', 'goblin_skirmisher'],
  goblin_screener: ['goblin_screener'], goblin_javelin: ['goblin_javelin'], goblin_skirmisher: ['goblin_skirmisher'],
  hobb: ['goblin_boss', 'goblin_captain', 'goblin_screener'],
};

/** Flatten a picks.json value (string | string[] | string[][] | {edge: string}) into frame names. */
function framesOf(v: unknown): string[] {
  if (typeof v === 'string') return [v];
  if (Array.isArray(v)) return v.flatMap(framesOf);
  if (v && typeof v === 'object') return Object.values(v as Record<string, unknown>).flatMap(framesOf);
  return [];
}

export function adaptOryxPicks(picks: unknown, cell = DEFAULT_CELL): AtlasManifest | undefined {
  if (!picks || typeof picks !== 'object') return undefined;
  const p = picks as Record<string, unknown>;
  const roles: AtlasManifest['roles'] = {};
  for (const role of ROLES) {
    for (const concept of ROLE_PICKS[role]) {
      const frames = framesOf(p[concept]).filter((f) => /^[a-z0-9]+_\d+$/.test(f));
      const first = frames[0];
      if (!first) continue;
      const entry: RoleFrame = { key: atlasForFrame(first), frame: first };
      if (frames.length > 1) entry.variants = frames.filter((f) => atlasForFrame(f) === entry.key);
      roles[role] = entry;
      break;
    }
  }
  if (Object.keys(roles).length < ROLES.length) return undefined;
  return { cell, atlases: Object.fromEntries(ORYX_ATLAS_KEYS.map((a) => [a, `/assets/oryx/${a}`])), roles };
}

export async function resolveManifest(fetchImpl: typeof fetch = fetch): Promise<AtlasManifest & { source: 'oryx' | 'dev' }> {
  try {
    const picksRes = await fetchImpl(ORYX_PICKS_URL, { cache: 'no-cache' });
    if (picksRes.ok && (picksRes.headers.get('content-type') ?? '').includes('json')) {
      let cell = DEFAULT_CELL;
      try {
        const m = await fetchImpl(ORYX_MANIFEST_URL, { cache: 'no-cache' });
        if (m.ok) { const j = (await m.json()) as { CELL?: number; cell?: number }; cell = j.CELL ?? j.cell ?? cell; }
      } catch { /* keep default */ }
      const adapted = adaptOryxPicks(await picksRes.json(), cell);
      if (adapted) {
        const probe = await fetchImpl(`${adapted.atlases['creatures']}.json`, { method: 'HEAD' });
        if (probe.ok) return { ...adapted, source: 'oryx' };
      }
    }
  } catch { /* fall through to the dev atlas */ }
  const dev = (await (await fetchImpl(DEV_MANIFEST_URL)).json()) as AtlasManifest;
  return { ...dev, source: 'dev' };
}

export function frameFor(manifest: AtlasManifest, role: string, variantSeed?: number): { key: string; frame: string } {
  const r = manifest.roles[role] ?? manifest.roles['floor'] ?? { key: Object.keys(manifest.atlases)[0] ?? 'dev', frame: 'floor' };
  if (variantSeed !== undefined && r.variants && r.variants.length > 0) {
    return { key: r.key, frame: r.variants[Math.abs(variantSeed) % r.variants.length]! };
  }
  return { key: r.key, frame: r.frame };
}

/**
 * Unit frame: the snapshot's Oryx `appearance` (creature frame index: heroes 1–5, goblins 253–257)
 * when the creatures atlas carries it, else the role's pick. `has` is the scene's texture lookup.
 */
export function unitFrame(manifest: AtlasManifest, unit: { sprite: string; appearance?: number; heroId?: number; faction?: string; classId?: string }, has: (key: string, frame: string) => boolean): { key: string; frame: string } {
  // docs/145 KH6: a hero is its kit look, never an Oryx frame, once the look is baked
  const kit = kitKeyOf(unit);
  if (kit && has(kit, '__BASE')) return { key: kit, frame: '__BASE' };
  // docs/92 HM5: a commissioned huge body is its own image, not an atlas frame
  const art = hugeArt(unit.appearance);
  if (art && has(art.key, '__BASE')) return { key: art.key, frame: '__BASE' };
  // docs/117 TT3: a commissioned one-square creature is its own 24 px image, drawn as a frame is
  const small = creatureArt(unit.appearance);
  if (small && has(small.key, '__BASE')) return { key: small.key, frame: '__BASE' };
  if (unit.appearance && manifest.atlases['creatures']) {
    const frame = `creature_${String(unit.appearance).padStart(3, '0')}`;
    if (has('creatures', frame)) return { key: 'creatures', frame };
  }
  return frameFor(manifest, unit.sprite);
}
